import React, { useEffect, useRef, useState } from 'react';

// One control per brokerage. The bar stays short no matter how many accounts
// someone has: the broker you're looking at shows its selected account, the
// others just show how many they hold, and the list only appears on click.
// Drag a control sideways to reorder; the order is remembered per portfolio.
const label = a => [a.name || a.account_type || 'account', a.number_masked].filter(Boolean).join(' ');

const KEY = owner => `df:brokers:${owner || 'me'}`;

const remembered = owner => {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY(owner)));
    return Array.isArray(raw) ? raw.filter(k => typeof k === 'string') : [];
  } catch {
    return [];
  }
};

const remember = (owner, order) => {
  try { localStorage.setItem(KEY(owner), JSON.stringify(order)); } catch { /* private mode */ }
};

export default function AccountPicker({ accounts, selected, onSelect }) {
  const [open, setOpen] = useState(null);   // broker key
  const [drag, setDrag] = useState(null);   // broker key being dragged
  const owner = accounts[0]?.owner;
  const [order, setOrder] = useState(() => remembered(owner));
  const ref = useRef(null);

  useEffect(() => { setOrder(remembered(owner)); }, [owner]);

  useEffect(() => {
    if (open == null) return undefined;
    const away = e => { if (!ref.current?.contains(e.target)) setOpen(null); };
    const esc = e => { if (e.key === 'Escape') setOpen(null); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, [open]);

  const brokers = [];
  for (const a of accounts) {
    const key = a.broker || 'broker';
    let group = brokers.find(g => g.key === key);
    if (!group) brokers.push((group = { key, accounts: [] }));
    group.accounts.push(a);
  }
  // Remembered brokers first, in their saved order; anything new keeps its natural place.
  const rank = k => (order.indexOf(k) === -1 ? order.length + brokers.findIndex(g => g.key === k) : order.indexOf(k));
  brokers.sort((a, b) => rank(a.key) - rank(b.key));

  const move = (from, to) => {
    if (from === to) return;
    const keys = brokers.map(g => g.key);
    keys.splice(keys.indexOf(to), 0, ...keys.splice(keys.indexOf(from), 1));
    setOrder(keys);
    remember(owner, keys);
  };

  // Keyboard equivalent of the drag, so this isn't mouse-only.
  const nudge = (key, step) => {
    const keys = brokers.map(g => g.key);
    const to = keys[keys.indexOf(key) + step];
    if (to) move(key, to);
  };

  return (
    <span className="df-brokers" ref={ref}>
      {brokers.map(({ key, accounts: mine }) => {
        const here = mine.find(a => a.id === selected);
        const one = mine.length === 1;
        const account = here || (one ? mine[0] : null);
        return (
          <span key={key} className={`df-broker ${here ? 'on' : ''} ${drag === key ? 'dragging' : ''}`}
            draggable onDragStart={e => {
              setDrag(key); setOpen(null);
              // Firefox won't start a drag without payload; jsdom has no dataTransfer at all.
              if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', key); }
            }}
            onDragEnd={() => setDrag(null)}
            onDragOver={e => { e.preventDefault(); if (drag && drag !== key) move(drag, key); }}
            onDrop={e => { e.preventDefault(); setDrag(null); }}>
            <button className="df-tab" aria-expanded={open === key} aria-haspopup={!one}
              title={`${mine.map(label).join(' · ')} — drag to reorder`}
              onKeyDown={e => {
                if (!e.shiftKey || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
                e.preventDefault();
                nudge(key, e.key === 'ArrowLeft' ? -1 : 1);
              }}
              onClick={() => (one ? onSelect(mine[0].id) : setOpen(o => (o === key ? null : key)))}>
              <span className={`df-dot ${(here || mine[0]).link_status}`} />
              <span className="df-tab-broker">{key}</span>
              {here ? label(here) : <span className="df-muted">{one ? label(account) : `${mine.length} accounts`}</span>}
              {!one && <span className="df-caret" aria-hidden="true">{open === key ? '▴' : '▾'}</span>}
            </button>

            {open === key && (
              <span className="df-acct-menu" role="listbox">
                {mine.map(a => (
                  <button key={a.id} role="option" aria-selected={a.id === selected}
                    className={a.id === selected ? 'on' : ''}
                    onClick={() => { onSelect(a.id); setOpen(null); }}>
                    <span className={`df-dot ${a.link_status}`} />
                    {a.name || a.account_type || 'account'}
                    {a.number_masked && <span className="df-muted"> {a.number_masked}</span>}
                  </button>
                ))}
              </span>
            )}
          </span>
        );
      })}
    </span>
  );
}
